/**
Complex math library.  Created by Collaborative Consulting and ADI in 2012.
Functions in this library are often used at the lowest level of our simulations, and are run thousands of times.  
It is critical that these functions run quickly.  
In 2019, we compared this library to the open source complex library, Complex.js.  This library came out after
we had created this homemade one.  Performance of this homemade library compared favorably to that library.  See ARNA-3487 for more details.

One drawback of this library is that most of the library is embedded each time a new complex number is 
created.  This has two ramifications:
1) Avoid putting more in the prototype than absolutely necessary (as it will increase the memory footprint)
2) We have created several "destroy" functions in this library.  These functions reuse the input variable to
   avoid creating a new object and are considerably faster.  However they destroy the input variable, so have
   to be used quite carefully.
*/

/**
@author Created by : Archita Ghosh on behalf of Collaborative Consulting and ADI.
@function Complex 
@param {int} real part of the complex number which is in the form of a+ib.
@param {int} imaginary part of the complex number.
@returns A complex number in the form of a+ib.
@description This function creates a complex number.
The parameters to this function are real part and the imaginary part.
*/
export function Complex(real, imaginary) {
  this.real = real;
  this.imaginary = imaginary;
}

Complex.prototype.toString = function () {
  return this.real + ' + ' + this.imaginary + 'i';
};

/**
@description  The constants converted into complex numbers which are used in the algorithm
*/
Complex['0'] = new Complex(0, 0);
Complex['1'] = new Complex(1, 0);
Complex['I'] = new Complex(0, 1, 1, Math.PI / 2);
Complex['PI'] = new Complex(Math.PI, 0, Math.PI, 0);
Complex['E'] = new Complex(Math.E, 0, Math.E, 0);

/**
@function plus
@param a complex number.
@returns summation of two complex numbers.
@description  This function takes as a parameter another complex number and returns a sum of two complex number
@example  Let the first complex number be x+iy and second complex number be a+ib.
The result of (x+iy).plus(a+ib) = (x+a) +i(y+b)
*/

Complex.prototype.plus = function (c) {
  return new Complex(this.real + c.real, this.imaginary + c.imaginary);
};

/*This addition function is much faster than the regular plus function, because it reuses the incoming variable instead of creating a new return variable.
However it destroys the original value in the variable in the process, so this function should be used with care.*/
Complex.prototype.plusDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;

  c.real = this.real + realPart;
  c.imaginary = this.imaginary + imagPart;
  return c;
};

/**
@function subs
@param a complex number.
@returns substraction of two complex numbers.
@description  This function takes as a parameter another complex number and returns a substraction of two complex number
@example  Let the first complex number be x+iy and second complex number be a+ib.
The result of (x+iy).subs(a+ib) = (x-a) +i(y-b)
*/

Complex.prototype.subs = function (c) {
  return new Complex(this.real - c.real, this.imaginary - c.imaginary);
};

/*This addition function is much faster than the regular subs function, because it reuses the incoming variable instead of creating a new return variable.
However it destroys the original value in the variable in the process, so this function should be used with care.*/
Complex.prototype.subsDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;

  c.real = this.real - realPart;
  c.imaginary = this.imaginary - imagPart;
  return c;
};

/**
@function mult
@param a complex number.
@returns multiplication of two complex numbers.
@description  This function takes as a parameter another complex number and returns a multiplication of two complex number
@example  Let the first complex number be x+iy and second complex number be a+ib.
The result of (x+iy).mult(a+ib) = (x*a)-(y*b) + i(y*a)+(b*x)
*/

Complex.prototype.mult = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;

  return new Complex(
    realPart * this.real - imagPart * this.imaginary,
    this.imaginary * realPart + imagPart * this.real,
  );
};

/*This multiply function is much faster than the regular mult function, because it reuses the incoming variable instead of creating a new return variable.
However it destroys the value of the original variable in the process, so this function should be used with care.*/
Complex.prototype.multDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;

  c.real = realPart * this.real - imagPart * this.imaginary;
  c.imaginary = this.imaginary * realPart + imagPart * this.real;

  return c;
};

/**
@function divide2
@param a complex number.
@returns division of two complex numbers.
@description  This function takes as a parameter another complex number and returns a division of two complex number

This is the old divide version that we used to use, but is a little slow because there are two divides.  No longer use 
this as of April 2019.  See ARNA-3487 for details.

We should probably remove divide2 at some point, but keeping for the time being in case something goes horribly wrong.
*/
Complex.prototype.divide2 = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;
  var divident = realPart * realPart + imagPart * imagPart;
  return new Complex(
    (this.real * realPart + this.imaginary * imagPart) / divident,
    (this.imaginary * realPart - this.real * imagPart) / divident,
  );
};

/** This is the new divide version that we started using in April 2019.  It's faster than the old version because it
 *  only uses one divide.  See ARNA-3487 for details
 */
Complex.prototype.divide = function (c) {
  //multiply top and bottom by complex conjugate
  var numer_real = c.real * this.real + this.imaginary * c.imaginary;
  var numer_imag = c.real * this.imaginary - c.imaginary * this.real;
  var denom_real = c.real * c.real + c.imaginary * c.imaginary;
  var denom_real_invert = 1 / denom_real;

  return new Complex(numer_real * denom_real_invert, numer_imag * denom_real_invert);
};

/*This divide function is much faster than the regular divide function, because it reuses the incoming variable.
    However it destroys the original variable in the process, so this function should be used with extreme care.*/
Complex.prototype.divideDestroy = function (c) {
  var realPart = c.real;
  var imagPart = c.imaginary;
  var divident = realPart * realPart + imagPart * imagPart;
  c.real = (this.real * realPart + this.imaginary * imagPart) / divident;
  c.imaginary = (this.imaginary * realPart - this.real * imagPart) / divident;

  return c;
};

/**
@function magnitude
@returns magnitude of a complex number
@description  This function calculates and returns the magnitude of the complex number.
@example  Let the complex number be x+iy 
The result of (x+iy).magnitude() = sqrt(x*x +y*y)
*/
Complex.prototype.magnitude = function () {
  return Math.sqrt(this.real * this.real + this.imaginary * this.imaginary);
};

/**
@function arctan
@param a complex number.
@returns inverse tangent of a complex number
@description  This function takes as a parameter another complex number and returns an inverse tangent of a complex number
*/
Complex.prototype.arctan = function (c) {
  var i = Complex.I;
  return i.mult(Complex.log(i.plus(c).divide(i.subs(c)))).divide(new Complex(2, 0, 2, 0));
};

/**
@function floor
@param a complex number.
@returns floor value of a complex number.
@description  This function takes as a parameter another complex number and returns a floor value of a complex number.
*/
Complex.prototype.floor = function (c) {
  return new Complex(Math.floor(c.real), Math.floor(c.imaginary));
};

/**
@function log
@param a complex number.
@returns log of a  complex numbers.
@description  This function takes as a parameter another complex number and returns a log of the complex number
*/
Complex.prototype.log = function (c) {
  return new Complex(Math.log(c.real), c.t + 2 * Math.PI * Math.floor(-c.t / (2 * Math.PI) + 1 / 2));
};

export function createComplex(real, imaginary) {
  return new Complex(real, imaginary);
}

//Make scalar value complex.  Handy for use with optional values, where you aren't sure if they are undefined
export function complexify(scalarValue) {
  if (scalarValue !== undefined) {
    return new Complex(scalarValue, 0);
  } else {
    return undefined;
  }
}

/**
@function complexfreq
@param {object} fp This is the object which contains the array of frequency points
@returns array of complex frequency points
@description <p> This function takes input as the array of frequency points and returns an array of complex frequency points used in the calculation.</p>
*<p><b>INPUT</b> : array of frequency points obtained</p>
*<p><b>OUTPUT</b> : array of complex frequency points</p>
*/
export function complexfreq(fp) {
  var complexfreq = new Array();
  for (var r = 0; r < fp.length; r++) {
    complexfreq[r] = new Complex(fp[r], 0);
  }

  return complexfreq;
}

/**
@function complexNumberRaisedToPower
@param complex number, integer power
@returns complexNumber
*/
export function complexNumberRaisedToPower(c, n) {
  var modC, theta, retComplex, realPart, imaginaryPart, modCpowern;

  // |x+iy| = Math.sqrt(x^2 + y^2) = |z|
  modC = Math.sqrt(c.real * c.real + c.imaginary * c.imaginary);

  // |x+iy|^n  = |z|^n
  modCpowern = Math.pow(modC, n);

  //theta=tan^(-1)(y/x)>
  theta = Math.atan2(c.imaginary, c.real);

  //|z|^n * cos(n* theta)
  realPart = modCpowern * Math.cos(n * theta);

  //|z|^n * sin(n* theta)
  imaginaryPart = modCpowern * Math.sin(n * theta);

  //zn = rn(cos(ntheta)+i sin(ntheta))
  retComplex = new Complex(realPart, imaginaryPart);

  return retComplex;
}
